import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BASE_URL } from '../api/api';
import PropTypes from 'prop-types';

const VoitureGalleryModal = ({
    mainPhoto,
    carteGrisePhoto,
    assurancePhoto,
    additionalPhotos = [],
    onClose
}) => {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'ar';

    const allPhotos = [
        { src: mainPhoto, type: t('main_photo_label'), label: t('main_photo_label') },
        { src: carteGrisePhoto, type: t('registration_card_label'), label: t('registration_card_label') },
        { src: assurancePhoto, type: t('insurance_label'), label: t('insurance_label') },
        ...additionalPhotos.map(photo => ({
            src: photo.photo_url,
            type: t('supplementary'),
            label: t('extra_photo_label')
        }))
    ].filter(photo => photo.src);

    const [selectedIndex, setSelectedIndex] = useState(0);

    if (allPhotos.length === 0) {
        return (
            <div className={`fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 ${isRtl ? 'rtl' : 'ltr'}`} dir={isRtl ? 'rtl' : 'ltr'}>
                <div className="bg-white rounded-lg p-8 max-w-2xl w-full">
                    <div className="text-center text-gray-500">
                        {t('no_photos')}
                    </div>
                    <button
                        onClick={onClose}
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 w-full"
                    >
                        {t('close')}
                    </button>
                </div>
            </div>
        );
    }

    const currentPhoto = allPhotos[selectedIndex];

    return (
        <div className={`fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4 overflow-auto ${isRtl ? 'rtl' : 'ltr'}`} dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className={`flex justify-between items-center border-b p-4 bg-gray-50 rounded-t-xl ${isRtl ? 'flex-row-reverse' : ''}`}>
                    <h2 className="text-xl font-bold text-gray-800">
                        {t('gallery_full')} - {currentPhoto.label}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-200 transition-colors"
                    >
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Main Content */}
                <div className="flex-1 overflow-auto p-6">
                    {/* Main Photo */}
                    <div className="relative bg-gray-100 rounded-lg overflow-hidden mb-4" style={{ height: '60vh' }}>
                        <img
                            src={`${BASE_URL}/uploads/${currentPhoto.src}`}
                            alt={currentPhoto.label}
                            className="w-full h-full object-contain"
                        />

                        {/* Navigation Arrows */}
                        {allPhotos.length > 1 && (
                            <>
                                <button
                                    onClick={() => setSelectedIndex(prev =>
                                        prev === 0 ? allPhotos.length - 1 : prev - 1
                                    )}
                                    className={`absolute ${isRtl ? 'right-4' : 'left-4'} top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70`}
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRtl ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => setSelectedIndex(prev =>
                                        prev === allPhotos.length - 1 ? 0 : prev + 1
                                    )}
                                    className={`absolute ${isRtl ? 'left-4' : 'right-4'} top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70`}
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRtl ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
                                    </svg>
                                </button>
                            </>
                        )}
                    </div>

                    {/* Photo Info */}
                    <div className={`flex justify-between items-center mb-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
                        <div className={`text-lg font-medium ${isRtl ? 'text-right' : 'text-left'}`}>
                            {currentPhoto.label}
                            {currentPhoto.type === t('supplementary') && (
                                <span className={`${isRtl ? 'mr-2' : 'ml-2'} text-sm text-gray-500`}>({t('supplementary')})</span>
                            )}
                        </div>
                        <div className="text-gray-600">
                            <span className="font-bold">{selectedIndex + 1}</span> / {allPhotos.length}
                        </div>
                    </div>
                </div>

                {/* Thumbnails Panel */}
                <div className="border-t p-4 bg-gray-50">
                    <div className={`flex overflow-x-auto py-2 ${isRtl ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                        {allPhotos.map((photo, index) => (
                            <button
                                key={index}
                                onClick={() => setSelectedIndex(index)}
                                className={`flex-shrink-0 relative w-24 h-20 rounded-md overflow-hidden border-2 ${selectedIndex === index ? 'border-blue-500' : 'border-gray-300'
                                    }`}
                            >
                                <img
                                    src={`${BASE_URL}/uploads/${photo.src}`}
                                    alt={`${t('thumbnail')} ${index + 1}`}
                                    className="w-full h-full object-cover"
                                />
                                <span className={`absolute bottom-1 ${isRtl ? 'right-1' : 'left-1'} bg-blue-500 text-white text-xs px-1 rounded`}>
                                    {photo.type}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

VoitureGalleryModal.propTypes = {
    mainPhoto: PropTypes.string,
    carteGrisePhoto: PropTypes.string,
    assurancePhoto: PropTypes.string,
    additionalPhotos: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                photo_url: PropTypes.string
            })
        ])
    ),
    onClose: PropTypes.func.isRequired
};

VoitureGalleryModal.defaultProps = {
    additionalPhotos: []
};

export default VoitureGalleryModal;
